import { Form } from "antd/es";
import type { FormInstance } from "antd/es/form";
import React from "react";
import { KMainFunctions } from "../shared/utilty/main-functions";

declare let window: any;

interface IMasterFormContextValue {
  form: FormInstance | null;
}

export const MasterFormContext = React.createContext<IMasterFormContextValue>({ form: null });

interface IMasterFormBridgeProps {
  children: React.ReactNode;
}

const MasterFormBridge: React.FC<IMasterFormBridgeProps> = ({ children }) => {
  const [form] = Form.useForm();
  return <MasterFormContext.Provider value={{ form }}>{children}</MasterFormContext.Provider>;
};

interface IMasterScreenWrapperLocationState {
  screenInputs?: Record<string, unknown>;
}

interface IMasterScreenWrapperLocation {
  search?: string;
  pathname?: string;
  state?: IMasterScreenWrapperLocationState;
}

interface IInlineFormScopeProps {
  children?: React.ReactNode;
}

// KOA-17353: form-scope isolation for drawer / modal / floatingPanel child screens that
// have NO master assigned. Such children would otherwise inherit the app-level
// MasterFormContext (set up by the root MasterScreenWrapper), which means their
// <Form.Item> values bind to the shared root form. Antd's <Form> defaults to
// preserve={true}, so unmounting fields on close keeps their values in the shared form;
// the next open re-mounts the fields and rehydrates the stale values. Result: drawer
// looked clean visually but the form state never reset without a page reload.
//
// Wrapping the master-less child in InlineFormScope gives it its own form instance.
// destroyOnClose unmounts the scope along with the child, disposing the form. The next
// open creates a fresh scope and a fresh form.
//
// Children that DO have a master are already isolated by `*_InlineWrapper` (itself a
// MasterScreenWrapper subclass that mounts its own MasterFormBridge), so they don't
// need this wrapper.
export const InlineFormScope: React.FC<IInlineFormScopeProps> = ({ children }) => {
  const [form] = Form.useForm();
  return <MasterFormContext.Provider value={{ form }}>{children}</MasterFormContext.Provider>;
};

interface IMasterScreenWrapperProps {
  children?: React.ReactNode;
  // KOA-17357: react-router-dom injects `location` into the routed `*_Wrapper` subclass when
  // it is mounted via `<Route component={...} />`. We forward it to the Master component so
  // its outer `withScreenInput` HOC can parse query/state into `props.screenInputs`. Without
  // this, screen inputs declared on the master page were always `{}` because `withScreenInput`
  // sits OUTSIDE `withHistory` in the master's HOC chain and runs before the `useLocation`
  // hook injects a location prop. Used by the currentPage / newTab / newWindow openAs paths.
  // Absent for inline-mounted (drawer/modal/floatingPanel) wrappers — they have no Route.
  location?: IMasterScreenWrapperLocation;
  // KOA-17357: for drawer/modal/floatingPanel (and any other non-currentPage / non-newTab /
  // non-newWindow openAs) the generated parent screen passes `window.kuika.navigationScreenInputs`
  // to the `*_InlineWrapper` so the master's `withScreenInput` HOC can populate via the
  // `props.screenInputs` source (the same channel the child screen uses). Undefined for
  // route-mounted wrappers; their screen inputs flow through `location` above.
  screenInputs?: Record<string, unknown>;
}

interface IMasterScreenWrapperState {
  privateRouteReady: boolean;
}

export abstract class MasterScreenWrapper extends React.PureComponent<
  IMasterScreenWrapperProps,
  IMasterScreenWrapperState
> {
  private _handler: (() => void) | null = null;

  constructor(props: IMasterScreenWrapperProps) {
    super(props);
    this.state = {
      privateRouteReady: window.userInfo !== undefined
    };
  }

  protected abstract get anonymousPaths(): string[];
  protected abstract get MasterComponent(): React.ComponentType<any>;
  protected abstract get MasterProvider(): React.ComponentType<any>;
  protected abstract renderSwitchWrapper(): React.ReactNode;

  // When true, render master immediately and skip the privateRouteReady auth gate.
  // The router-level wrapper leaves this false (default) so master chrome doesn't flash
  // before login resolves. Inline-mount subclasses (drawer/modal/floatingPanel) override
  // to true: by definition they only mount inside an already-routed (already-authed)
  // parent screen, so the auth gate is meaningless and can incorrectly suppress master
  // when window.userInfo isn't readable at construction time for any reason.
  protected get forceReady(): boolean {
    return false;
  }

  componentDidMount() {
    if (this.state.privateRouteReady) return;
    this._handler = () => this.setState({ privateRouteReady: true });
    window.addEventListener("privateRouteContextReady", this._handler);
  }

  componentWillUnmount() {
    if (this._handler) {
      window.removeEventListener("privateRouteContextReady", this._handler);
      this._handler = null;
    }
  }

  private isPathAnonymous(): boolean {
    const currentPath = window.location.hash
      ? window.location.hash.replace(/^#/, "").split("?")[0]
      : (() => {
          const virtualDir = KMainFunctions.virtualDirectoryURL === "auto" ? "" : KMainFunctions.virtualDirectoryURL;
          return window.location.pathname.replace(virtualDir, "").split("?")[0];
        })();
    return this.anonymousPaths.some((p) => currentPath?.toLowerCase() === p);
  }

  render() {
    const { privateRouteReady } = this.state;
    const switchContent = this.renderSwitchWrapper();

    if (!this.forceReady && !privateRouteReady && !this.isPathAnonymous()) {
      return switchContent;
    }

    const Master = this.MasterComponent;
    const Provider = this.MasterProvider;

    return (
      <MasterFormBridge>
        <Provider>
          <Master location={this.props.location} screenInputs={this.props.screenInputs}>
            {switchContent}
          </Master>
        </Provider>
      </MasterFormBridge>
    );
  }
}
